import React, { useState, useEffect, useRef } from "react";
import ReusableTable from "../../dashboard/ReusableTable";
import { getRequest, postRequest } from "../../services/apiService";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReusableForm from "../../common/ReusableForm";
import ReusableDeleteDialog from '../../common/ReusableDeleteDialog';

const DepartmentMaster = () => {
  const [departments, setDepartments] = useState([]);
  const [addDepartmentDialog, setAddDepartmentDialog] = useState(false);
  const [editDepartmentDialog, setEditDepartmentDialog] = useState(false);
  const [department, setDepartment] = useState({
    id: null,
    code: "",
    name: "",
    status_db: 1,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState(null);
  const [deleteFormData, setDeleteFormData] = useState({ confirmation: '' });

  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirmation",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  const departmentFormFields = [
    {
      name: "code",
      label: "Department Code",
      type: "text",
      required: true,
      placeholder: "Enter department code"
    },
    {
      name: "name",
      label: "Department Name",
      type: "text",
      required: true,
      placeholder: "Enter department name"
    },
  ];

  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);

  const loadDepartments = () => {
    setLoading(true);
    getRequest(`master/departments/?search=${encodeURIComponent(search)}&page=${currentPage}`)
      .then(response => {
        setDepartments(Array.isArray(response.results?.data) ? response.results.data : []);
        setTotalRecords(response.count || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      loadDepartments();
    }, 700);
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [search, currentPage]);

  const handleTableSearch = (value) => {
    setSearch(value);
  };

  const resetDepartmentData = () => {
    setDepartment({
      id: null,
      code: "",
      name: "",
      status_db: 1,
    });
  };

  const openAddDialog = () => {
    resetDepartmentData();
    setAddDepartmentDialog(true);
  };

  const openEditDialog = (rowData) => {
    setDepartment({ ...rowData });
    setEditDepartmentDialog(true);
  };

  const onPageChange = (event) => {
    setCurrentPage(event.page + 1);
    setRowsPerPage(event.rows);
  };

  const saveDepartment = async () => {
    if (!department.code || !department.name) {
      toast.error("Department code and name are required");
      return;
    }

    try {
      const payload = {
        ...department,
        status_db: department.status_db || 1,
      };

      await postRequest("master/departments/", payload);

      if (department.id) {
        toast.success("Department updated successfully");
      } else {
        toast.success("Department added successfully");
        setCurrentPage(1);
      }

      setAddDepartmentDialog(false);
      setEditDepartmentDialog(false);
      resetDepartmentData();
      loadDepartments();
    } catch (error) {
      console.error("Error saving department:", error);
      if (error.message.includes("400")) {
        toast.error("Validation error: Check the data being sent");
      } else {
        toast.error(`Failed to save department: ${error.message}`);
      }
    }
  };

  const handleDelete = async () => {
    if (deleteFormData.confirmation !== 'DELETE') {
      toast.error("Please type 'DELETE' to confirm");
      return;
    }
    if (departmentToDelete) {
      try {
        setLoading(true);
        const payload = {
          ...departmentToDelete,
          status_db: 3
        };
        await postRequest("master/departments/", payload);
        toast.success("Department deleted successfully");

        if (departments.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1); // Triggers useEffect
        } else {
          loadDepartments(); // Explicit refresh
        }

        setDeleteConfirmationOpen(false);
        setDepartmentToDelete(null);
        setDeleteFormData({ confirmation: '' });
      } catch (error) {
        console.error("Error deleting department:", error);
        toast.error(`Failed to delete department: ${error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const confirmDelete = (rowData) => {
    setDepartmentToDelete(rowData);
    setDeleteFormData({ confirmation: '' });
    setDeleteConfirmationOpen(true);
  };

  const actionItems = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: (rowData) => openEditDialog(rowData),
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: (rowData) => confirmDelete(rowData),
    },
  ];

  const columnFields = [
    { field: "name", header: "Department Name" },
    { field: "code", header: "Dept. Code" },
  ];

  return (
    <div className="p-4">
      <ToastContainer />

      <ReusableTable
        title="Department"
        data={departments}
        setData={setDepartments}
        columnFields={columnFields}
        setAddNewDialogVisible={openAddDialog}
        actionMenu={true}
        actionItems={actionItems}
        loading={loading}
        totalRecords={totalRecords}
        rows={rowsPerPage}
        onPageChange={onPageChange}
        first={(currentPage - 1) * rowsPerPage}
        searchValue={search}
        onSearchChange={handleTableSearch}
      />

      {addDepartmentDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-2xl w-full mx-4 animate-fade-in max-w-lg">
            <ReusableForm
              formData={department}
              setFormData={setDepartment}
              fields={departmentFormFields}
              onSubmit={saveDepartment}
              onHide={() => setAddDepartmentDialog(false)}
              loading={loading}
              headerText="Add New Department"
            />
          </div>
        </div>
      )}

      {editDepartmentDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-2xl w-full mx-4 animate-fade-in max-w-lg">
            <ReusableForm
              formData={department}
              setFormData={setDepartment}
              fields={departmentFormFields}
              onSubmit={saveDepartment}
              onHide={() => setEditDepartmentDialog(false)}
              loading={loading}
              headerText="Edit Department"
            />
          </div>
        </div>
      )}

      <ReusableDeleteDialog
        visible={deleteConfirmationOpen}
        onHide={() => setDeleteConfirmationOpen(false)}
        onDelete={handleDelete}
        fields={deleteFormFields}
        formData={deleteFormData}
        setFormData={setDeleteFormData}
        loading={loading}
        title="Delete Department"
        description={`Are you sure you want to delete department "${departmentToDelete?.name}"? This action cannot be undone. Type 'DELETE' to confirm.`}
        deleteLabel="Delete"
      />
    </div>
  );
};

export default DepartmentMaster;
